import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/utils";
import { BridgeDataItem, LeaderboardData, BridgePlanData } from '../types';

const BRIDGE_COLORS = ["#F97316", "#10B981", "#0EA5E9", "#FBBF24", "#EC4899", "#8B5CF6", "#D946EF", "#EF4444", "#14B8A6", "#F59E0B", "#6366F1", "#84CC16", "#D946EF", "#84CC16"];

export const useBridgeData = () => {
  const [bridgeData, setBridgeData] = useState<BridgeDataItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const calculateBridgeData = (leaderboardData: LeaderboardData[], bridgePlanData: BridgePlanData[]) => {
    const ytdRevenue = leaderboardData.reduce((sum, record) => sum + record.achievement, 0);
    const targetRevenue = leaderboardData.reduce((sum, record) => sum + record.target, 0);
    const bridgeItems: BridgeDataItem[] = [{
      name: "YTD Revenue",
      value: ytdRevenue,
      fill: "#FFFF00",
      isStart: true,
      displayValue: formatCurrency(ytdRevenue),
      iconName: "Coins"
    }];
    let totalBridgedRevenue = ytdRevenue;
    bridgePlanData.forEach((item, index) => {
      if (item.bridge_component === "Target Revenue") return;
      if (item.bridge_component === "Total Bridged Revenue") return;
      if (item.bridge_component === "Order to Revenue Conversion Rate") return;
      const value = item.bridge_value;
      totalBridgedRevenue += value;
      let iconName;
      switch (item.bridge_component) {
        case "Late Stage Wins":
          iconName = value >= 0 ? "TrendingUp" : "TrendingDown";
          break;
        case "High-Performing Accounts":
        case "High Win-Rate Accounts":
          iconName = value >= 0 ? "ArrowUp" : "TrendingDown";
          break;
        case "Recovery of Low Performers":
          iconName = value >= 0 ? "TrendingUp" : "TrendingDown";
          break;
        case "Product Promotions":
          iconName = value >= 0 ? "Gift" : "TrendingDown";
          break;
        case "Special Discounts":
          iconName = value >= 0 ? "Tag" : "TrendingDown";
          break;
        default:
          iconName = value >= 0 ? "TrendingUp" : "TrendingDown";
      }
      bridgeItems.push({
        name: item.bridge_component,
        value: value,
        fill: BRIDGE_COLORS[index % BRIDGE_COLORS.length],
        displayValue: value >= 0 ? `+${formatCurrency(value)}` : formatCurrency(value),
        iconName: iconName,
        description: item.bridge_description || "",
        id: item.id,
        sn: item.SN
      });
    });
    bridgeItems.push({
      name: "Total Bridged Revenue",
      value: totalBridgedRevenue,
      fill: "#8B5CF6",
      isTotalBridged: true,
      displayValue: formatCurrency(totalBridgedRevenue),
      iconName: "TrendingUp"
    });
    bridgeItems.push({
      name: "Target Revenue",
      value: targetRevenue,
      fill: "#33C3F0",
      isTarget: true,
      displayValue: formatCurrency(targetRevenue),
      iconName: "Target"
    });
    setBridgeData(bridgeItems);
  };

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        throw new Error(`Error fetching user: ${userError.message}`);
      }

      const {
        data: leaderboardData,
        error: leaderboardError
      } = await supabase.from("leaderboard").select("*");
      if (leaderboardError) {
        throw new Error(`Error fetching leaderboard data: ${leaderboardError.message}`);
      }
      if (!leaderboardData || leaderboardData.length === 0) {
        throw new Error("No leaderboard data found");
      }
      
      const {
        data: bridgePlanData,
        error: bridgePlanError
      } = await supabase.from("bridgeplan").select("*").order("SN", {
        ascending: true
      });
      
      if (bridgePlanError) {
        throw new Error(`Error fetching bridge plan data: ${bridgePlanError.message}`);
      }
      if (!bridgePlanData || bridgePlanData.length === 0) {
        throw new Error("No bridge plan data found");
      }
      
      calculateBridgeData(leaderboardData, bridgePlanData);
    } catch (err) {
      console.error("Error in bridge plan:", err);
      setError(err.message || "Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = async () => {
    const {
      data: leaderboardData,
      error: leaderboardError
    } = await supabase.from("leaderboard").select("*");
    if (leaderboardError) {
      throw new Error(`Error fetching leaderboard data: ${leaderboardError.message}`);
    }
    const {
      data: bridgePlanData,
      error: bridgePlanError
    } = await supabase.from("bridgeplan").select("*").order("SN", {
      ascending: true
    });
    if (bridgePlanError) {
      throw new Error(`Error fetching bridge plan data: ${bridgePlanError.message}`);
    }
    calculateBridgeData(leaderboardData, bridgePlanData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    bridgeData,
    setBridgeData,
    isLoading,
    error,
    refreshData
  };
};
